import type { StatTestType } from './test-library-datasets.model';

// ---------------------------------------------------------------------------
// Generator config
// ---------------------------------------------------------------------------

export interface TestGeneratorConfig {
  readonly testType: StatTestType;
  readonly effectStrength: number;
  readonly sampleSize: number;
  readonly decimalPlaces: number;
  readonly hypothesizedMean: number;
  readonly equalGroupSizes: boolean;
  readonly contingencyRows: number;
  readonly contingencyCols: number;
  readonly gofCategories: number;
  readonly seed: string;
}

export const DEFAULT_TEST_CONFIG: TestGeneratorConfig = {
  testType: 'one-sample-t',
  effectStrength: 0.5,
  sampleSize: 30,
  decimalPlaces: 1,
  hypothesizedMean: 100,
  equalGroupSizes: true,
  contingencyRows: 2,
  contingencyCols: 3,
  gofCategories: 6,
  seed: '',
};

// ---------------------------------------------------------------------------
// Presets & options
// ---------------------------------------------------------------------------

export const EFFECT_PRESETS = [0, 0.2, 0.5, 0.8] as const;
export const EFFECT_PRESET_LABELS = ['None', 'Small', 'Med', 'Large'] as const;
export const SAMPLE_SIZE_PRESETS = [10, 20, 30, 50, 100] as const;
export const DECIMAL_PLACES_OPTIONS = [0, 1, 2] as const;

export interface ContingencySizeOption {
  readonly label: string;
  readonly rows: number;
  readonly cols: number;
}

export const CONTINGENCY_SIZE_OPTIONS: readonly ContingencySizeOption[] = [
  { label: '2 \u00D7 2', rows: 2, cols: 2 },
  { label: '2 \u00D7 3', rows: 2, cols: 3 },
  { label: '3 \u00D7 3', rows: 3, cols: 3 },
  { label: '3 \u00D7 4', rows: 3, cols: 4 },
];

export const GOF_CATEGORY_OPTIONS = [4, 5, 6, 8] as const;

// ---------------------------------------------------------------------------
// Conditional visibility helpers
// ---------------------------------------------------------------------------

export function needsHypothesizedMean(type: StatTestType): boolean {
  return type === 'one-sample-t';
}

export function needsGroupSizeToggle(type: StatTestType): boolean {
  return type === 'two-sample-t';
}

export function needsContingencySize(type: StatTestType): boolean {
  return type === 'chi-squared-independence';
}

export function needsGofCategories(type: StatTestType): boolean {
  return type === 'chi-squared-gof';
}

export function isChiSquared(type: StatTestType): boolean {
  return type === 'chi-squared-independence' || type === 'chi-squared-gof';
}

// ---------------------------------------------------------------------------
// Effect size mapping & display
// ---------------------------------------------------------------------------

export function getEffectLabel(type: StatTestType): string {
  switch (type) {
    case 'one-sample-t':
    case 'two-sample-t':
    case 'paired-t':
      return 'EFFECT SIZE (Cohen\u2019s d)';
    case 'chi-squared-independence':
      return 'ASSOCIATION (Cram\u00E9r\u2019s V)';
    case 'chi-squared-gof':
      return 'DEVIATION STRENGTH';
    case 'sign-test':
      return 'BIAS STRENGTH';
  }
}

export function getEffectMetricLabel(type: StatTestType): string {
  switch (type) {
    case 'one-sample-t':
    case 'two-sample-t':
    case 'paired-t':
      return 'd';
    case 'chi-squared-independence':
      return 'V';
    case 'chi-squared-gof':
      return 'dev';
    case 'sign-test':
      return 'p';
  }
}

export function getEffectMetricValue(type: StatTestType, strength: number): string {
  switch (type) {
    case 'one-sample-t':
    case 'two-sample-t':
    case 'paired-t':
      return (strength * 2).toFixed(2);
    case 'chi-squared-independence':
      return (strength * 0.5).toFixed(2);
    case 'chi-squared-gof':
      return strength.toFixed(2);
    case 'sign-test':
      return (0.5 + strength * 0.45).toFixed(2);
  }
}
